import React, { useState, useEffect } from 'react';
import { releasesAPI, uploadAPI } from '../../services/api';
import '../../App.css';

function ReleasesAdmin() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    artist: '',
    title: '',
    description: '',
    image: '',
  });

  useEffect(() => {
    loadReleases();
  }, []);

  const loadReleases = async () => {
    try {
      const response = await releasesAPI.getAll();
      // Ensure we always have an array
      const data = response?.data || [];
      // Reverse to show newest first in admin
      const releasesArray = Array.isArray(data) ? data : [];
      setReleases([...releasesArray].reverse());
    } catch (error) {
      console.error('Error loading releases:', error);
      alert('Error loading releases');
      setReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('File is too large. Maximum size is 10MB.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = formData.image;

      // Upload image if file is selected
      if (imageFile) {
        const uploadResponse = await uploadAPI.uploadImage(imageFile);
        imageUrl = uploadResponse.data.url;
      }

      const submitData = {
        ...formData,
        image: imageUrl,
      };

      if (editingId) {
        await releasesAPI.update(editingId, submitData);
      } else {
        await releasesAPI.create(submitData);
      }
      
      setFormData({ artist: '', title: '', description: '', image: '' });
      setImageFile(null);
      setImagePreview('');
      setEditingId(null);
      loadReleases();
      alert('Release saved!');
    } catch (error) {
      console.error('Error saving release:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error saving release';
      if (errorMessage.includes('too large') || errorMessage.includes('LIMIT_FILE_SIZE')) {
        alert('File is too large. Maximum size is 10MB.');
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (release) => {
    setFormData({
      artist: release.artist || '',
      title: release.title || '',
      description: release.description || '',
      image: release.image || '',
    });
    setImageFile(null);
    setImagePreview(release.image || '');
    setEditingId(release.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this release?')) {
      try {
        await releasesAPI.delete(id);
        loadReleases();
      } catch (error) {
        console.error('Error deleting release:', error);
        alert('Error deleting release');
      }
    }
  };

  const handleMove = async (id, direction) => {
    try {
      await releasesAPI.move(id, direction);
      loadReleases();
    } catch (error) {
      console.error('Error moving release:', error);
      alert(error.response?.data?.error || 'Error moving release');
    }
  };

  const handleCancel = () => {
    setFormData({ artist: '', title: '', description: '', image: '' });
    setImageFile(null);
    setImagePreview('');
    setEditingId(null);
  };

  if (loading) {
    return <div className="empty-state">Loading releases...</div>;
  }

  return (
    <div>
      <h2 style={{ fontFamily: 'Bungee, cursive', marginBottom: '20px', color: '#228b22' }}>
        {editingId ? 'EDIT RELEASE' : 'ADD NEW RELEASE'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Artist Name</label>
          <input
            type="text"
            value={formData.artist}
            onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Album Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Image (optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && (
            <div style={{ marginTop: '10px' }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
              />
            </div>
          )}
          {formData.image && !imageFile && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ fontSize: '0.9rem', color: '#a0a0a0' }}>Current: {formData.image}</p>
              <img 
                src={formData.image} 
                alt="Current" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', marginTop: '5px' }}
              />
            </div>
          )}
        </div>
        <button type="submit" className="btn" disabled={uploading}>
          {uploading ? 'UPLOADING...' : editingId ? 'UPDATE RELEASE' : 'ADD RELEASE'}
        </button>
        {editingId && (
          <button type="button" className="btn btn-secondary" onClick={handleCancel}>
            CANCEL
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={() => setFormData({ artist: '', title: '', description: '', image: '' })}>
          CLEAR
        </button>
      </form>

      <div className="items-list">
        {!Array.isArray(releases) || releases.length === 0 ? (
          <div className="empty-state">No releases yet. Add one above!</div>
        ) : (
          releases.map((release) => (
            <div key={release.id} className="item-card">
              <h3>{release.artist}</h3>
              <p><strong>Title:</strong> {release.title}</p>
              <p><strong>Description:</strong> {release.description}</p>
              {release.image && <p><strong>Image:</strong> {release.image}</p>}
              <div className="item-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(release.id, 'up')}
                  disabled={releases.indexOf(release) === 0}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Up"
                >
                  ↑
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(release.id, 'down')}
                  disabled={releases.indexOf(release) === releases.length - 1}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Down"
                >
                  ↓
                </button>
                <button className="btn btn-secondary" onClick={() => handleEdit(release)}>
                  EDIT
                </button>
                <button className="btn btn-danger" onClick={() => handleDelete(release.id)}>
                  DELETE
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ReleasesAdmin;
